'use client'

import { useState } from 'react'
import { CMSLink } from '@/components/Link'
import Link from 'next/link'
import { ChevronsRight } from 'lucide-react'
import SubMenu from './submenu'

interface LinkType {
  label: string
  url: string
  newTab?: boolean
}

interface NavItem {
  link: LinkType
  description?: string
  subMenuItems?: any[] // Ideally, this would be typed more strictly as well
}

interface CtaItem {
  id: string
  link: LinkType
}

export const Header1 = ({
  navItems,
  logo,
  rightCta,
}: {
  navItems: NavItem[]
  logo: { url: string; alt?: string }
  rightCta?: CtaItem[]
}) => {
  const [hoveredMenu, setHoveredMenu] = useState<string | null>(null)

  const [hideTimeout, setHideTimeout] = useState<any>(null)
  const handleMouseEnter = (menu: any) => {
    if (hideTimeout) {
      clearTimeout(hideTimeout)
      setHideTimeout(null)
    }

    setHoveredMenu(menu)
  }

  const handleMouseLeave = () => {
    const timeout = setTimeout(() => {
      setHoveredMenu(null)
    }, 150) // 150ms delay before hiding
    setHideTimeout(timeout)
  }

  return (
    <header className="bg-background border-b border-border py-5 px-4 shadow-md font-sans">
      <div className="max-w-7xl mx-auto flex items-center">
        {logo?.url && (
          <Link href="/">
            <img src={logo.url} alt={logo.alt || 'Company Logo'} className="w-32 h-auto" />
          </Link>
        )}
        <nav className="flex flex-1 justify-end">
          <ul className="flex gap-12 items-center list-none">
            {navItems?.map((item) => (
              <li
                className="text-left cursor-pointer relative group/link"
                onMouseEnter={() => handleMouseEnter(item.link.label)}
                onMouseLeave={handleMouseLeave}
                key={item.link.label}
              >
                <Link href={item.link.url || '/'}>
                  <span className="text-xl font-bold text-foreground group-hover/link:text-destructive transition-colors">
                    {item.link.label}
                  </span>
                  {item.description && (
                    <span className="block text-muted-foreground group-hover/link:text-destructive/75 text-sm">
                      {item.description}
                    </span>
                  )}
                </Link>

                {hoveredMenu === item.link.label && !!item.subMenuItems?.length && (
                  <SubMenu
                    items={item.subMenuItems}
                    hideTimeout={hideTimeout}
                    setHideTimeout={setHideTimeout}
                  />
                )}
              </li>
            ))}
            {rightCta?.map((item) => (
              <li key={item.id}>
                <div className="relative overflow-hidden group rounded-full inline-flex">
                  <CMSLink
                    {...item.link}
                    className="z-10 inline-flex items-center border border-border text-foreground cursor-pointer hover:text-background font-semibold text-base tracking-wide transition-colors duration-300 group/link rounded-full px-6 py-8 bg-background hover:bg-foreground"
                  >
                    <div className="w-12 h-12 rounded-full flex flex-1 items-center justify-center ml-2 bg-destructive hover:text-destructive text-background">
                      <ChevronsRight />
                    </div>
                  </CMSLink>
                  <div className="absolute inset-0 bg-foreground origin-center rotate-[-45deg] scale-y-0 group-hover:scale-y-[5] transition-transform duration-500 ease-in-out rounded-full z-0" />
                </div>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </header>
  )
}
